import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileDown, ListPlus, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { nodePath } from '../lib/contracts'
import { formatMoney, isZeroMoney } from '../lib/money'
import { priceSections, unitPrice } from '../lib/priceItems'
import type { CostTree } from '../lib/tree'
import type { CostContractPriceItem, CostContractPriceTotals } from '../types'

interface Props {
  items: CostContractPriceItem[]
  totals: CostContractPriceTotals
  tree: CostTree
  currency: string
  locale: string
  canEdit: boolean
  canImport: boolean
  showBudgetItem: boolean
  onEdit: () => void
  onImport: () => void
}

/** "Rozpis ceny" (2026-10-06 spec §8): the contract's priced items, read-only, by section. */
const ContractPriceCard: React.FC<Props> = ({ items, totals, tree, currency, locale, canEdit, canImport, showBudgetItem, onEdit, onImport }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  const sections = priceSections(items)
  if (items.length === 0 && !canEdit && !canImport) return null
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{t('CostControl_price_title')}</CardTitle>
          {totals.unmappedCount > 0 && <Badge variant="warning">{t('CostControl_price_unmapped_count', { count: totals.unmappedCount })}</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canImport && (
            <Button variant="outline" size="sm" onClick={onImport}>
              <FileDown className="h-4 w-4" />
              {t('CostControl_price_import')}
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              {items.length ? <Pencil className="h-4 w-4" /> : <ListPlus className="h-4 w-4" />}
              {t(items.length ? 'CostControl_price_edit' : 'CostControl_price_create')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('CostControl_price_empty')}</p>
        ) : (
          <div>
            <Table containerClassName="max-h-[60vh]">
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  <TableHead>{t('CostControl_price_col_name')}</TableHead>
                  <TableHead className="text-right">{t('CostControl_price_col_quantity')}</TableHead>
                  <TableHead className="text-right">{t('CostControl_price_col_unit_price')}</TableHead>
                  <TableHead className="text-right">{t('CostControl_price_col_amount')}</TableHead>
                  <TableHead className="text-right">{t('CostControl_price_col_discount')}</TableHead>
                  <TableHead className="text-right">{t('CostControl_price_col_final')}</TableHead>
                  {showBudgetItem && <TableHead>{t('CostControl_price_col_budget_item')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sections.map((s) => (
                  <React.Fragment key={s.key}>
                    {s.section !== null && (
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableCell colSpan={5} className="font-medium">{s.section}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">{money(s.subtotal)}</TableCell>
                        {showBudgetItem && <TableCell />}
                      </TableRow>
                    )}
                    {s.items.map((i) => {
                      const node = i.nodeId ? tree.byId.get(i.nodeId)?.activity : undefined
                      const price = unitPrice(i.amount, i.quantity)
                      return (
                        <TableRow key={i.id}>
                          <TableCell>
                            {i.code && <span className="mr-2 font-mono text-xs text-muted-foreground">{i.code}</span>}
                            {i.name}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right tabular-nums">{i.quantity !== null ? [i.quantity.toLocaleString(locale), i.unit].filter(Boolean).join(' ') : ''}</TableCell>
                          <TableCell className="text-right tabular-nums">{price ? money(price) : ''}</TableCell>
                          <TableCell className="text-right tabular-nums">{money(i.amount)}</TableCell>
                          <TableCell className="text-right tabular-nums">{i.discountPercent && !isZeroMoney(i.discountPercent) ? `${Number(i.discountPercent).toLocaleString(locale)} %` : ''}</TableCell>
                          <TableCell className="text-right tabular-nums">{money(i.finalAmount)}</TableCell>
                          {showBudgetItem && (
                          <TableCell>
                            {node ? (
                              <>
                                <div className="text-xs text-muted-foreground">{nodePath(tree, node.id)}</div>
                                <div>{node.code ? `${node.code} · ${node.name}` : node.name}</div>
                              </>
                            ) : (
                              <Badge variant="warning">{t('CostControl_price_unmapped')}</Badge>
                            )}
                          </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
              <TableFooter className="sticky bottom-0 z-10 bg-muted">
                {!isZeroMoney(totals.contractDiscount) && (
                  <>
                    <TableRow>
                      <TableCell colSpan={5}>{t('CostControl_price_after_item_discounts')}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(totals.afterItemDiscounts)}</TableCell>
                      {showBudgetItem && <TableCell />}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5}>{t('CostControl_price_contract_discount')}</TableCell>
                      <TableCell className="text-right tabular-nums">−{money(totals.contractDiscount)}</TableCell>
                      {showBudgetItem && <TableCell />}
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell colSpan={5} className="font-semibold">{t('CostControl_price_total')}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(totals.total)}</TableCell>
                  {showBudgetItem && <TableCell />}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
export default ContractPriceCard
